/**
 * Interventions inbox — waiting_input / waiting_approval (REQ-FRT-016 §bandeja;
 * Sprint 308 Stream D). Respond, approve or reject with SLA visibility.
 */

import { useState } from 'react';
import { Inbox } from 'lucide-react';
import { PageHeader } from '@vanguard-atlas/shared';
import { Button, Card, CardContent, Spinner, Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow } from '@/components/ui';
import { useI18n } from '@/i18n';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { formatDateTime } from '../components/FrontierGuard';
import { useInterventions, useRespondIntervention } from '../hooks/useFrontier';

export default function FrontierInboxPage() {
  const { t } = useI18n();
  const canOperate = useModuleAccess('frontier', 'operate');
  const { data, isLoading, isError } = useInterventions({ status: 'open' });
  const respond = useRespondIntervention();
  const [answerFor, setAnswerFor] = useState<string | null>(null);
  const [answerText, setAnswerText] = useState('');

  const act = (interventionId: string, action: 'approve' | 'reject' | 'answer', text?: string) =>
    respond.mutate({ interventionId, action, text });

  return (
    <div className="p-6 space-y-4" data-testid="frontier-inbox">
      <PageHeader icon={Inbox} title={t('frontier.inbox.title')} testId="frontier-inbox-header" />
      {respond.isError && (
        <p className="text-sm text-red-600" role="alert">
          {t('frontier.inbox.error')}
        </p>
      )}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <Spinner size="sm" />
          ) : isError ? (
            <p className="text-sm text-red-600" role="alert">
              {t('frontier.actions.failed')}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('frontier.inbox.colRoutine')}</TableHead>
                  <TableHead>{t('frontier.inbox.colKind')}</TableHead>
                  <TableHead>{t('frontier.inbox.colQuestion')}</TableHead>
                  <TableHead>{t('frontier.inbox.colSla')}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data ?? []).length === 0 && (
                  <TableEmpty colSpan={5} title={t('frontier.inbox.empty')} />
                )}
                {(data ?? []).map((item) => (
                  <TableRow key={item.id} data-testid={`intervention-row-${item.id}`}>
                    <TableCell className="font-medium">{item.routine_name ?? '—'}</TableCell>
                    <TableCell>{t(`frontier.inbox.kind.${item.kind}`, { defaultValue: item.kind })}</TableCell>
                    <TableCell className="max-w-xs truncate">{item.question ?? '—'}</TableCell>
                    <TableCell>{formatDateTime(item.sla_expires_at) ?? '—'}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        {canOperate && (
                          <>
                            <Button
                              size="sm"
                              data-testid={`intervention-approve-${item.id}`}
                              disabled={respond.isPending}
                              onClick={() => act(item.id, 'approve')}
                            >
                              {t('frontier.inbox.approve')}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`intervention-reject-${item.id}`}
                              disabled={respond.isPending}
                              onClick={() => act(item.id, 'reject')}
                            >
                              {t('frontier.inbox.reject')}
                            </Button>
                            {item.kind === 'input' && (
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid={`intervention-answer-${item.id}`}
                                onClick={() => {
                                  setAnswerFor(item.id);
                                  setAnswerText('');
                                }}
                              >
                                {t('frontier.inbox.answer')}
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {answerFor && (
        <Card>
          <CardContent className="space-y-3 pt-6">
            <label className="text-sm font-medium" htmlFor="intervention-answer">
              {t('frontier.inbox.answerLabel')}
            </label>
            <textarea
              id="intervention-answer"
              data-testid="intervention-answer-text"
              className="w-full rounded-lg border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-900"
              rows={3}
              value={answerText}
              onChange={(event) => setAnswerText(event.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setAnswerFor(null)}>
                {t('frontier.editor.cancel')}
              </Button>
              <Button
                size="sm"
                data-testid="intervention-answer-send"
                disabled={respond.isPending || !answerText.trim()}
                onClick={() => {
                  act(answerFor, 'answer', answerText.trim());
                  setAnswerFor(null);
                }}
              >
                {t('frontier.inbox.send')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
